/**
 * Represents mmd animation data
 *
 * Internally, it uses typed arrays to store animation data for better performance
 *
 * Therefore, it is not compatible with existing Babylon.js animation systems.
 *
 * You can save one mesh animation and one camera animation in one `MmdAnimation` instance
 */
export class MmdAnimation {
    /**
     * Animation name for identification
     */
    name;
    /**
     * Bone animation tracks for one `mesh.skeleton`
     *
     * it contains rotation and rotation cubic interpolation data
     */
    boneTracks;
    /**
     * Moveable bone animation tracks for one `mesh.skeleton`
     *
     * it contains position, rotation and their cubic interpolation data
     */
    moveableBoneTracks;
    /**
     * Morph animation tracks for one `mesh.morphTargetManager`
     *
     * it contains weight and weight linear interpolation data
     */
    morphTracks;
    /**
     * Property animation track for one `mmdModel`
     *
     * it contains visibility and ik toggle keyframe data
     */
    propertyTrack;
    /**
     * Camera animation track for one `mmdCamera`
     *
     * it contains position, rotation, distance and fov cubic interpolation data
     */
    cameraTrack;
    /**
     * The start frame of this animation
     */
    startFrame;
    /**
     * The end frame of this animation
     */
    endFrame;
    /**
     * Create a new `MmdAnimation` instance
     * @param name animation name for identification
     * @param boneTracks bone animation tracks
     * @param moveableBoneTracks moveable bone animation tracks
     * @param morphTracks morph animation tracks
     * @param propertyTrack property animation track
     * @param cameraTrack camera animation track
     */
    constructor(name, boneTracks, moveableBoneTracks, morphTracks, propertyTrack, cameraTrack) {
        this.name = name;
        this.boneTracks = boneTracks;
        this.moveableBoneTracks = moveableBoneTracks;
        this.morphTracks = morphTracks;
        this.propertyTrack = propertyTrack;
        this.cameraTrack = cameraTrack;
        let minStartFrame = Number.MAX_SAFE_INTEGER;
        let maxEndFrame = Number.MIN_SAFE_INTEGER;
        for (let i = 0; i < boneTracks.length; ++i) {
            const boneTrack = boneTracks[i];
            minStartFrame = Math.min(minStartFrame, boneTrack.startFrame);
            maxEndFrame = Math.max(maxEndFrame, boneTrack.endFrame);
        }
        for (let i = 0; i < moveableBoneTracks.length; ++i) {
            const moveableBoneTrack = moveableBoneTracks[i];
            minStartFrame = Math.min(minStartFrame, moveableBoneTrack.startFrame);
            maxEndFrame = Math.max(maxEndFrame, moveableBoneTrack.endFrame);
        }
        for (let i = 0; i < morphTracks.length; ++i) {
            const morphTrack = morphTracks[i];
            minStartFrame = Math.min(minStartFrame, morphTrack.startFrame);
            maxEndFrame = Math.max(maxEndFrame, morphTrack.endFrame);
        }
        minStartFrame = Math.min(minStartFrame, propertyTrack.startFrame);
        maxEndFrame = Math.max(maxEndFrame, propertyTrack.endFrame);
        minStartFrame = Math.min(minStartFrame, cameraTrack.startFrame);
        maxEndFrame = Math.max(maxEndFrame, cameraTrack.endFrame);
        this.startFrame = minStartFrame;
        this.endFrame = maxEndFrame;
    }
    /**
     * Check if all animation tracks are valid(sorted)
     * @returns true if all animation tracks are valid
     */
    validate() {
        const boneTracks = this.boneTracks;
        for (let i = 0; i < boneTracks.length; ++i) {
            if (!boneTracks[i].validate())
                return false;
        }
        const moveableBoneTracks = this.moveableBoneTracks;
        for (let i = 0; i < moveableBoneTracks.length; ++i) {
            if (!moveableBoneTracks[i].validate())
                return false;
        }
        const morphTracks = this.morphTracks;
        for (let i = 0; i < morphTracks.length; ++i) {
            if (!morphTracks[i].validate())
                return false;
        }
        return this.propertyTrack.validate() && this.cameraTrack.validate();
    }
}
